import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { resolvePage } from '../api/renderer';
import { trackPageView } from '../api/tracking';
import type { PagePayload } from '../types/pagePayload';
import LoadingPage from '../components/LoadingPage';
import ErrorPage from '../components/ErrorPage';
import NotFoundPage from './NotFoundPage';
import EmptySitePage from './EmptySitePage';
import TemplateLoader from '../templates/TemplateLoader';

export default function PageRenderer() {
  const location = useLocation();
  const [payload, setPayload] = useState<PagePayload | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [emptySiteName, setEmptySiteName] = useState<string | null>(null);
  const [suspended, setSuspended] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;

    setPayload(null);
    setNotFound(false);
    setEmptySiteName(null);
    setSuspended(false);
    setError(null);

    resolvePage(location.pathname)
      .then((response) => {
        if (cancelled) return;

        if ('page' in response) {
          setPayload(response);
          trackPageView(location.pathname);
          return;
        }

        if (response.path === 'suspended') {
          setSuspended(true);
          return;
        }
        if (response.path === 'empty_site') {
          setEmptySiteName(response.website.name);
          return;
        }

        setNotFound(true);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError);
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (error) {
    return <ErrorPage message="Unable to load this page." />;
  }

  if (suspended) {
    return <main style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:'2rem',fontFamily:'system-ui, sans-serif',textAlign:'center'}}>
      <section><h1>Website temporarily unavailable</h1><p>This website is currently unavailable. Please contact the website administrator.</p></section>
    </main>;
  }
  if (emptySiteName) {
    return <EmptySitePage websiteName={emptySiteName} />;
  }

  if (notFound) {
    return <NotFoundPage />;
  }

  if (!payload) {
    return <LoadingPage />;
  }

  return <TemplateLoader payload={payload} />;
}
